/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Input, NgZone, OnChanges} from '@angular/core';
import template from './sorting-header.component.html';
import {AppEventBusService} from '../../events/app-event-bus.service';

import {AdminListColumn} from '../../controller/admin-resource-list-page-base';
import {AdnResourceListService} from '../../controller/adn-resource-list.service';

export type AdnSortingHeaderParam = string | AdminListColumn;

@Component({
  standalone: false,
  selector: 'tr[adn-list-sort-header-row]',
  template,
  host: {
    role: 'row',
    class: 'listRowHeader'
  }
})
export class AdnSortingHeaderComponent implements OnChanges {
  @Input() list: any;
  /** Legacy callers pass string or param objects via params. */
  @Input() params: AdnSortingHeaderParam[] = [];
  /** List pages pass normalised AdminListColumn arrays. */
  @Input() columns: AdminListColumn[];
  @Input() hideActions: boolean | string;
  @Input() extraField: boolean | string;
  @Input() translationPrefix = 'label';

  hideActionsValue = false;

  constructor(
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly listService: AdnResourceListService,
    private readonly eventBus: AppEventBusService
  ) {
  }

  ngOnChanges(): void {
    this.hideActionsValue = this.coerceBoolean(this.hideActions);
    if (this.list) {
      this.listService.ensureAngularList(this.list);
    }
  }

  get headerColumns(): AdnSortingHeaderParam[] {
    return this.columns?.length ? this.columns : this.params;
  }

  columnTranslateKey(paramValue: AdnSortingHeaderParam): string {
    if (this.columns?.length) {
      return this.columnLabel(paramValue);
    }
    return this.translationPrefix + '.' + this.labelKey(paramValue);
  }

  sortParam(paramValue: AdnSortingHeaderParam): string {
    if (typeof paramValue === 'string') {
      return paramValue;
    }
    return paramValue.param ?? '';
  }

  labelKey(paramValue: AdnSortingHeaderParam): string {
    if (typeof paramValue === 'string') {
      return paramValue;
    }
    return paramValue.label || paramValue.param || '';
  }

  labelInstant(paramValue: AdnSortingHeaderParam): string | undefined {
    if (typeof paramValue === 'string') {
      return undefined;
    }
    return paramValue.labelInstant;
  }

  isNumeric(paramValue: AdnSortingHeaderParam): boolean {
    return typeof paramValue !== 'string' && !!paramValue.numeric;
  }

  isSortable(paramValue: AdnSortingHeaderParam): boolean {
    if (typeof paramValue !== 'string' && paramValue.sortable === false) {
      return false;
    }
    return !!this.sortParam(paramValue).length;
  }

  columnLabel(paramValue: AdnSortingHeaderParam): string {
    if (typeof paramValue === 'string') {
      return 'label.' + paramValue;
    }
    const key = paramValue.label || paramValue.param;
    return key ? 'label.' + key : '';
  }

  sort(paramValue: AdnSortingHeaderParam, event?: Event): void {
    event?.preventDefault();
    if (!this.list || typeof this.list.makeSortQuery !== 'function') {
      return;
    }
    const param = this.sortParam(paramValue);
    if (!param.length) {
      return;
    }
    this.ngZone.run(() => {
      this.list.makeSortQuery(param, this.isNumeric(paramValue) || undefined);
      this.afterListQuery(this.list.sortQuery);
    });
  }

  private afterListQuery(query: any): void {
    const promise = query?.$promise || query;
    if (promise?.then) {
      promise.then(
        () => this.refreshFromListQuery(),
        () => this.refreshFromListQuery()
      );
      return;
    }
    this.refreshFromListQuery();
  }

  private refreshFromListQuery(): void {
    this.ngZone.run(() => {
      this.cdr.detectChanges();
      this.eventBus?.runAsync();
    });
  }

  private coerceBoolean(value: boolean | string | undefined): boolean {
    return value === '' || value === true || value === 'true';
  }
}
